// src/pages/ErrorLogsPage.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { api } from '../services/api';
import { useToast } from '../hooks/useToast';
import { InlineMessage } from '../components/common/InlineMessage';

interface ErrorLog {
  id: number;
  timestamp: string;
  category: string;
  severity: string;
  message: string;
  stackTrace?: string;
  statusCode?: number;
  userId?: number;
  httpMethod?: string;
  requestPath?: string;
  context?: string;
  isReviewed: boolean;
  reviewNote?: string;
  errorFingerprint?: string;
  occurrenceCount?: number;
  lastOccurrence?: string;
}

interface Statistics {
  bySeverity: Record<string, number>;
  byCategory: Record<string, number>;
}

export const ErrorLogsPage: React.FC = () => {
  const [errors, setErrors] = useState<ErrorLog[]>([]);
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(50);
  const [filterSeverity, setFilterSeverity] = useState<string>('');
  const [filterCategory, setFilterCategory] = useState<string>('');
  const [hoursFilter, setHoursFilter] = useState<number>(24);
  const [selectedError, setSelectedError] = useState<ErrorLog | null>(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewNote, setReviewNote] = useState('');
  const [showUniqueOnly, setShowUniqueOnly] = useState(true);

  const { showSuccess, showError: showErrorToast } = useToast();

  const fetchErrors = async () => {
    setLoading(true);
    setError(null);

    try {
      // TODO: showUniqueOnly only affects the request when hoursFilter is
      // explicitly 0 ("All Time") and no severity/category filter is set -
      // otherwise the `else if (hoursFilter > 0)` branch below always wins
      // (hoursFilter defaults to 24), silently overriding the unique/recent
      // endpoint choice. Confirmed on the backend: /errorlogs/last-hours
      // (GetRecentErrorsAsync) is never deduplicated, so with the page's
      // default filters the "Unique Errors" switch has zero effect. Needs
      // a product decision (disable the switch when hours-filtered, or add
      // an `hours` param to /unique and /recent) before fixing - see chat.
      let endpoint = showUniqueOnly ? '/errorlogs/unique' : '/errorlogs/recent';
      let params = `?pageNumber=${pageNumber}&pageSize=${pageSize}`;

      if (filterSeverity) {
        endpoint = `/errorlogs/by-severity/${filterSeverity}`;
        params = `?pageNumber=${pageNumber}&pageSize=${pageSize}`;
      } else if (filterCategory) {
        endpoint = `/errorlogs/by-category/${filterCategory}`;
        params = `?pageNumber=${pageNumber}&pageSize=${pageSize}`;
      } else if (hoursFilter > 0) {
        endpoint = '/errorlogs/last-hours';
        params = `?hours=${hoursFilter}`;
      }

      const response = await api.get(endpoint + params);
      setErrors(response.data.errors || []);
    } catch (err: any) {
      const message = err.response?.data?.message || 'Failed to fetch errors';
      setError(message);
      showErrorToast(message);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await api.get('/errorlogs/statistics');
      setStatistics(response.data);
    } catch {
      // fail silently — statistics are non-critical
    }
  };

  useEffect(() => {
    fetchErrors();
    fetchStatistics();
  }, []);

  useEffect(() => {
    setPageNumber(1);
    fetchErrors();
  }, [filterSeverity, filterCategory, hoursFilter, showUniqueOnly]);

  const handleMarkReviewed = async () => {
    if (!selectedError) return;

    try {
      await api.put(`/errorlogs/${selectedError.id}/mark-reviewed`, {
        reviewNote,
      });

      showSuccess();
      setReviewDialogOpen(false);
      setReviewNote('');
      setSelectedError(null);
      fetchErrors();
      fetchStatistics();
    } catch (err: any) {
      const message =
        err.response?.data?.message || 'Failed to mark error as reviewed';
      showErrorToast(message);
    }
  };

  const getSeverityColor = (
    severity: string
  ): 'error' | 'warning' | 'info' | 'success' => {
    switch (severity) {
      case 'Critical':
        return 'error';
      case 'Warning':
        return 'warning';
      case 'Info':
        return 'info';
      default:
        return 'success';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'Critical':
        return <ErrorIcon fontSize="small" />;
      case 'Warning':
        return <WarningIcon fontSize="small" />;
      case 'Info':
        return <InfoIcon fontSize="small" />;
      default:
        return <CheckCircleIcon fontSize="small" />;
    }
  };

  const formatTimestamp = (timestamp: string) =>
    new Date(timestamp).toLocaleString();

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Error Logs Dashboard
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Monitor and manage application errors{' '}
          {showUniqueOnly && '(Deduplicated View)'}
        </Typography>
      </Box>

      {error && (
        <InlineMessage
          message={error}
          severity="error"
          dismissible
          onClose={() => setError(null)}
        />
      )}

      {statistics && (
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {Object.entries(statistics.bySeverity).map(([severity, count]) => (
            <Grid item xs={12} sm={6} md={3} key={`severity-${severity}`}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getSeverityIcon(severity)}
                    <Box>
                      <Typography color="textSecondary" variant="caption">
                        {severity}
                      </Typography>
                      <Typography variant="h6">{count}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm={6} md={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={showUniqueOnly}
                  onChange={(e) => setShowUniqueOnly(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2">
                    {showUniqueOnly ? 'Unique Errors' : 'All Errors'}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {showUniqueOnly
                      ? 'Grouped by fingerprint'
                      : 'All occurrences'}
                  </Typography>
                </Box>
              }
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Time Range</InputLabel>
              <Select
                value={hoursFilter}
                label="Time Range"
                onChange={(e) => setHoursFilter(Number(e.target.value))}
              >
                <MenuItem value={1}>Last Hour</MenuItem>
                <MenuItem value={24}>Last 24 Hours</MenuItem>
                <MenuItem value={168}>Last Week</MenuItem>
                <MenuItem value={0}>All Time</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Filter by Severity</InputLabel>
              <Select
                value={filterSeverity}
                label="Filter by Severity"
                onChange={(e) => setFilterSeverity(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="Info">Info</MenuItem>
                <MenuItem value="Warning">Warning</MenuItem>
                <MenuItem value="Error">Error</MenuItem>
                <MenuItem value="Critical">Critical</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Filter by Category</InputLabel>
              <Select
                value={filterCategory}
                label="Filter by Category"
                onChange={(e) => setFilterCategory(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="Validation">Validation</MenuItem>
                <MenuItem value="Database">Database</MenuItem>
                <MenuItem value="Authentication">Authentication</MenuItem>
                <MenuItem value="Server">Server</MenuItem>
                <MenuItem value="Network">Network</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={() => {
                fetchErrors();
                fetchStatistics();
              }}
              disabled={loading}
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table>
            <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
              <TableRow>
                <TableCell>Timestamp</TableCell>
                <TableCell>Severity</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Message</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>HTTP Method</TableCell>
                <TableCell>Path</TableCell>
                {showUniqueOnly && (
                  <TableCell align="center">Occurrences</TableCell>
                )}
                {showUniqueOnly && <TableCell>Last Seen</TableCell>}
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {errors.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={showUniqueOnly ? 10 : 8}
                    align="center"
                    sx={{ py: 3 }}
                  >
                    <Typography color="textSecondary">
                      No errors found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                errors.map((errorLog) => (
                  <TableRow
                    key={errorLog.id}
                    sx={{
                      backgroundColor: errorLog.isReviewed ? '#f9f9f9' : '#fff',
                      '&:hover': { backgroundColor: '#f5f5f5' },
                    }}
                  >
                    <TableCell sx={{ fontSize: '0.85rem' }}>
                      {formatTimestamp(errorLog.timestamp)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getSeverityIcon(errorLog.severity)}
                        label={errorLog.severity}
                        size="small"
                        color={getSeverityColor(errorLog.severity)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={errorLog.category}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        maxWidth: 250,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {errorLog.message}
                    </TableCell>
                    <TableCell>{errorLog.statusCode || '-'}</TableCell>
                    <TableCell>{errorLog.httpMethod || '-'}</TableCell>
                    <TableCell
                      sx={{
                        maxWidth: 200,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {errorLog.requestPath || '-'}
                    </TableCell>
                    {showUniqueOnly && (
                      <TableCell align="center">
                        <Chip
                          label={`${errorLog.occurrenceCount || 1}x`}
                          size="small"
                          color={
                            (errorLog.occurrenceCount || 1) > 10
                              ? 'error'
                              : 'default'
                          }
                        />
                      </TableCell>
                    )}
                    {showUniqueOnly && (
                      <TableCell sx={{ fontSize: '0.85rem' }}>
                        {errorLog.lastOccurrence
                          ? formatTimestamp(errorLog.lastOccurrence)
                          : formatTimestamp(errorLog.timestamp)}
                      </TableCell>
                    )}
                    <TableCell>
                      <Button
                        size="small"
                        variant={errorLog.isReviewed ? 'outlined' : 'contained'}
                        onClick={() => {
                          setSelectedError(errorLog);
                          setReviewNote(errorLog.reviewNote || '');
                          setReviewDialogOpen(true);
                        }}
                      >
                        {errorLog.isReviewed ? 'Reviewed' : 'Review'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      <Dialog
        open={reviewDialogOpen}
        onClose={() => setReviewDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Mark Error as Reviewed</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {selectedError && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Error Message:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  mb: 2,
                  p: 1,
                  backgroundColor: '#f5f5f5',
                  borderRadius: 1,
                }}
              >
                {selectedError.message}
              </Typography>

              {selectedError.occurrenceCount && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Occurrences:{' '}
                    <strong>{selectedError.occurrenceCount}x</strong>
                  </Typography>
                </Box>
              )}

              <Typography variant="subtitle2" gutterBottom>
                Stack Trace:
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  mb: 2,
                  p: 1,
                  backgroundColor: '#f5f5f5',
                  borderRadius: 1,
                  display: 'block',
                  maxHeight: 150,
                  overflow: 'auto',
                  fontFamily: 'monospace',
                }}
              >
                {selectedError.stackTrace || 'No stack trace available'}
              </Typography>
            </Box>
          )}

          <TextField
            fullWidth
            label="Review Note"
            multiline
            rows={3}
            value={reviewNote}
            onChange={(e) => setReviewNote(e.target.value)}
            placeholder="Add notes about this error..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleMarkReviewed} variant="contained">
            Mark as Reviewed
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ErrorLogsPage;
